/**
 * pt を人が入れず、他の種別から導く種別。
 *
 * 比率の出どころは「開発の規模（Develop）が決まれば前後の工数も決まる」という
 * 見積もりの運用そのもの。ここに無い種別（Develop / Basic Design Review /
 * Release Procedure 系 / UAT Execution / Release / App Release）は人が入れる。
 * @graph-connects none
 */
export const AUTO_POINT_KIND_IDS = new Set([
    'basic-design',
    'detailed-design',
    'detailed-design-review',
    'it-creation',
    'it-creation-review',
    'it-execution',
    'dev-buffer',
    'it-buffer',
    'uat-buffer',
    'code-review',
]);
/** 「影響範囲調査」の行は設計の密度が低いので、詳細設計の比率を下げる。 @graph-connects none */
const IMPACT_SURVEY_ITEM = '影響範囲調査';
/**
 * pt を自動で決める行の id を返す。
 *
 * 自動計算の種別でも、**自動で入るのは同じ種別の 1 行目だけ**。2 行目以降は人が入れる。
 * 同じ種別を何行にも割るのは「この種別の作業をさらに細かく分けた」ときで、分け方の
 * 配分まで計算では決められない（1 行目の値を各行に配ると、行を足すだけで合計が
 * 勝手に増える）。1 行目を計算の土台にし、増やした行は人が決める形にする。
 * @graph-connects none
 */
export function autoPointRowIds(rows) {
    const seen = new Set();
    const ids = new Set();
    for (const row of rows) {
        if (!AUTO_POINT_KIND_IDS.has(row.kindId) || seen.has(row.kindId))
            continue;
        seen.add(row.kindId);
        ids.add(row.rowId);
    }
    return ids;
}
/**
 * その行の pt を人が入れるのか、自動で決まるのかを返す。並びの中での位置で変わるので、
 * 行だけでなく全体を渡す。
 * @graph-connects autoPointRowIds [calls] 自動で決まる行の特定
 */
export function isAutoPointRow(rows, row) {
    return autoPointRowIds(rows).has(row.rowId);
}
/**
 * pt の文字列を数として読む。空・入力途中は 0 として扱う。
 * @graph-connects none
 */
function readPoint(point) {
    const value = Number.parseFloat(point);
    return Number.isFinite(value) ? value : 0;
}
/** 四捨五入して整数にする。pt は整数でしか扱わない運用に合わせる。 @graph-connects none */
function toInteger(value) {
    return Math.round(value);
}
/**
 * 行ごとの pt を確定させる。自動計算の種別の **1 行目だけ**計算値を入れ、
 * それ以外（2 行目以降・自動でない種別）は入力値をそのまま返す。
 *
 * 依存の順番は Develop → バッファ → 基本設計 → 詳細設計 → それ以降で、Develop だけが
 * 人の入力。ここから一方向に決まる（循環しない）。
 *
 * **他の種別を参照するときは、その種別の「合計」を見る**（1 行目の計算値 + 2 行目以降の
 * 手入力）。仕様の「develop と dev buffer の pt 総和 × 0.2」等がそもそも合計で書かれており、
 * 1 行目だけを見ると、人が足した行のぶんが下流の計算から丸ごと落ちる。
 *
 * @graph-connects autoPointRowIds [calls] 自動で決まる行の特定
 */
export function resolveChildTicketPoints(rows) {
    const autoIds = autoPointRowIds(rows);
    const rowsOf = (kindId) => rows.filter((row) => row.kindId === kindId);
    const sumManual = (kindId) => rowsOf(kindId).reduce((sum, row) => sum + readPoint(row.point), 0);
    /** その種別の合計。1 行目は計算値、2 行目以降は手入力。行が無ければ 0。 */
    const totalOf = (kindId, unit) => {
        const list = rowsOf(kindId);
        if (list.length === 0)
            return 0;
        const tail = list.slice(1).reduce((sum, row) => sum + readPoint(row.point), 0);
        return unit + tail;
    };
    // Develop だけが人の入力。ここから全部が決まる。
    const developTotal = sumManual('develop');
    // バッファ 3 種は同じ比率。基本設計は Dev Buffer を足した合計から出すので先に確定させる。
    const bufferUnit = toInteger(developTotal * 0.05);
    const devBufferTotal = totalOf('dev-buffer', bufferUnit);
    const basicDesignUnit = toInteger((developTotal + devBufferTotal) * 0.2);
    const basicDesignTotal = totalOf('basic-design', basicDesignUnit);
    const codeReviewUnit = toInteger(developTotal * 0.1);
    // 詳細設計は「項目」で比率が変わる。比率を当てるのは自動で決まる 1 行目だけ。
    const firstDetailedDesign = rowsOf('detailed-design')[0];
    const detailedDesignUnit = firstDetailedDesign === undefined
        ? 0
        : toInteger(basicDesignTotal * (firstDetailedDesign.item === IMPACT_SURVEY_ITEM ? 0.2 : 0.5));
    const detailedDesignTotal = totalOf('detailed-design', detailedDesignUnit);
    const detailedDesignReviewUnit = toInteger(detailedDesignTotal * 0.4);
    const itCreationUnit = toInteger(basicDesignTotal * 0.7);
    const itCreationTotal = totalOf('it-creation', itCreationUnit);
    const itCreationReviewUnit = toInteger(itCreationTotal * 0.4);
    /** 自動で決まる 1 行目に入れる値。 */
    const unitOf = (kindId) => {
        switch (kindId) {
            case 'basic-design':
                return basicDesignUnit;
            case 'detailed-design':
                return detailedDesignUnit;
            case 'detailed-design-review':
                return detailedDesignReviewUnit;
            case 'it-creation':
                return itCreationUnit;
            case 'it-creation-review':
                return itCreationReviewUnit;
            case 'it-execution':
                return itCreationTotal;
            case 'code-review':
                return codeReviewUnit;
            default:
                // dev-buffer / it-buffer / uat-buffer
                return bufferUnit;
        }
    };
    const resolved = new Map();
    for (const row of rows) {
        resolved.set(row.rowId, autoIds.has(row.rowId) ? unitOf(row.kindId) : readPoint(row.point));
    }
    return resolved;
}
/**
 * 1 まとまりに載せてよい pt の上限。これを超えたら、分けて進める合図にする
 * （チャンクに切り出す判断もここを見る）。
 * @graph-connects none
 */
export const CHILD_TICKET_POINT_LIMIT = 80;
/**
 * 合計 pt が上限を超えているか。
 * @graph-connects none
 */
export function isOverChildTicketPointLimit(total) {
    return total > CHILD_TICKET_POINT_LIMIT;
}
/**
 * 確定した pt の合計。
 * @graph-connects resolveChildTicketPoints [calls] 行ごとの確定 pt
 */
export function totalResolvedPoint(rows) {
    const resolved = resolveChildTicketPoints(rows);
    return [...resolved.values()].reduce((sum, value) => sum + value, 0);
}
//# sourceMappingURL=basic-design-points.js.map